'use client';

import { useEffect, useRef, useState } from 'react';

export type TickDirection = 'up' | 'down' | null;

export interface TickFlash {
  /** Direction of the LAST change (null until the first change). */
  dir: TickDirection;
  /** Monotonic change counter — use as a React `key` so the CSS flash
   * animation restarts on every tick, including same-direction ones. */
  seq: number;
}

/**
 * Tracks a live number across renders and reports the direction of each
 * change, for the price-blink treatment: put `tickFlashClass(f)` on the cell
 * and `key={f.seq}` so the `.tick-up` / `.tick-down` background wash
 * (globals.css) re-fires per tick. The animation fades itself out — no timer
 * or reset state needed here.
 */
export function useTickFlash(value: number | null | undefined): TickFlash {
  const prev = useRef<number | null>(null);
  const [flash, setFlash] = useState<TickFlash>({ dir: null, seq: 0 });
  useEffect(() => {
    const v = typeof value === 'number' && Number.isFinite(value) ? value : null;
    if (v === null) {
      return;
    }
    const p = prev.current;
    prev.current = v;
    if (p === null || v === p) {
      return;
    }
    const dir: TickDirection = v > p ? 'up' : 'down';
    setFlash((f) => ({ dir, seq: f.seq + 1 }));
  }, [value]);
  return flash;
}

/** The flash class for the last tick (empty string before the first change). */
export function tickFlashClass(f: TickFlash): string {
  if (f.dir === 'up') {
    return 'tick-up';
  }
  if (f.dir === 'down') {
    return 'tick-down';
  }
  return '';
}
